import React from "react";

// Generic skeleton loader component
const SkeletonLoader = ({ className = "", lines = 3, showAvatar = false }) => {
  const skeletonLines = Array.from({ length: lines }, (_, i) => (
    <div
      key={i}
      className={`h-4 bg-gray-200 rounded animate-pulse ${
        i === 0 ? "w-3/4" : i === 1 ? "w-1/2" : "w-5/6"
      }`}
      style={{ animationDelay: `${i * 0.1}s` }}
    />
  ));

  return (
    <div className={`space-y-3 ${className}`}>
      {showAvatar && (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/4" />
          </div>
        </div>
      )}
      <div className="space-y-2">{skeletonLines}</div>
    </div>
  );
};

// Step-specific skeleton loaders
export const StepSkeletonLoader = ({ stepId }) => {
  const getSkeletonForStep = (id) => {
    switch (id) {
      case "strategic-insight":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/3" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 6 }, (_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case "time-management":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 7 }, (_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case "lead-flow":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/3" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Array.from({ length: 8 }, (_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4" />
                <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
          </div>
        );

      case "business-info":
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4" />
                  <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4" />
                  <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
                <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4" />
                <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
          </div>
        );

      case "lead-response":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 4 }, (_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case "client-value":
      case "missed-lead-cost":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 6 }, (_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case "team-structure":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/3" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 4 }, (_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case "automation-preferences":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 8 }, (_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case "primary-goal":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/3" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 6 }, (_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case "contact-info":
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
                <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4" />
                <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-1/3" />
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
                <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header skeleton */}
          <div className="mb-8 space-y-3">
            <div className="h-8 bg-gray-200 rounded animate-pulse w-1/2" />
            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
          </div>

          {/* Step content skeleton */}
          {getSkeletonForStep(stepId)}

          {/* Footer skeleton */}
          <div className="mt-8 flex justify-between">
            <div className="h-10 bg-gray-200 rounded-lg animate-pulse w-24" />
            <div className="h-10 bg-gray-200 rounded-lg animate-pulse w-32" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Card skeleton for metrics and insights
export const CardSkeleton = ({ cards = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {Array.from({ length: cards }, (_, i) => (
      <div key={i} className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
          <div className="h-8 bg-gray-200 rounded animate-pulse w-1/4" />
          <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

// Button skeleton
export const ButtonSkeleton = ({ width = "w-32" }) => (
  <div className={`h-10 bg-gray-200 rounded-lg animate-pulse ${width}`} />
);

// Input skeleton
export const InputSkeleton = ({ label = true }) => (
  <div className="space-y-2">
    {label && <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4" />}
    <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
  </div>
);

// Table skeleton
export const TableSkeleton = ({ rows = 5, cols = 4 }) => (
  <div className="space-y-3">
    {/* Header */}
    <div className="grid grid-cols-4 gap-4">
      {Array.from({ length: cols }, (_, i) => (
        <div key={i} className="h-4 bg-gray-200 rounded animate-pulse" />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }, (_, rowIndex) => (
      <div key={rowIndex} className="grid grid-cols-4 gap-4">
        {Array.from({ length: cols }, (_, colIndex) => (
          <div
            key={colIndex}
            className={`h-4 bg-gray-200 rounded animate-pulse ${
              colIndex === 0 ? "w-3/4" : colIndex === 1 ? "w-1/2" : "w-full"
            }`}
          />
        ))}
      </div>
    ))}
  </div>
);

export default SkeletonLoader;
